import { useCallback, useState } from 'react';
import { createContainer } from 'unstated-next';
import { useTranslation } from '../i18n/i18n';
import { ApiFacetLoop54 } from '../types';

export const standardFacetNames: Record<string, string> = {
	inStock: '',
	campaign: '',
	ecoCount: '',
};

export const FILTER_RANGE_VALUE_DELIMITER = '->';
export const FILTER_MULTI_VALUE_DELIMITER = ';';

type RangeFacet = Extract<ApiFacetLoop54, { type: 'range' }>;

const hasSelectedRangeBoundary = (value?: number | null): value is number => value !== undefined && value !== null;

const matchesDefaultBoundary = (selected: number | undefined, defaultValue: number | undefined) => {
	if (selected === undefined || defaultValue === undefined) {
		return true;
	}

	return Number(selected) === Number(defaultValue);
};

const computeDefaultBounds = (facet: RangeFacet, initialPriceMinMax?: [number, number]) => {
	const defaultMin =
		facet.name === 'price' && initialPriceMinMax
			? initialPriceMinMax[0]
			: typeof facet.min === 'number'
				? facet.min
				: undefined;
	const defaultMax =
		facet.name === 'price' && initialPriceMinMax
			? initialPriceMinMax[1]
			: typeof facet.max === 'number'
				? facet.max
				: undefined;

	return { defaultMin, defaultMax };
};

export const isRangeFacetApplied = (facet: RangeFacet, initialPriceMinMax?: [number, number]) => {
	const hasSelectedMin = hasSelectedRangeBoundary(facet.selectedMin);
	const hasSelectedMax = hasSelectedRangeBoundary(facet.selectedMax);

	if (!hasSelectedMin && !hasSelectedMax) {
		return false;
	}

	const { defaultMin, defaultMax } = computeDefaultBounds(facet, initialPriceMinMax);

	const minMatchesDefault = hasSelectedMin ? matchesDefaultBoundary(facet.selectedMin, defaultMin) : true;
	const maxMatchesDefault = hasSelectedMax ? matchesDefaultBoundary(facet.selectedMax, defaultMax) : true;

	return !(minMatchesDefault && maxMatchesDefault);
};

export const resolveRangeSelectionForFacet = (facet: RangeFacet, initialPriceMinMax?: [number, number]) => {
	if (!isRangeFacetApplied(facet, initialPriceMinMax)) {
		return null;
	}

	const hasSelectedMin = hasSelectedRangeBoundary(facet.selectedMin);
	const hasSelectedMax = hasSelectedRangeBoundary(facet.selectedMax);
	const { defaultMin, defaultMax } = computeDefaultBounds(facet, initialPriceMinMax);

	const resolvedMin = hasSelectedMin ? Number(facet.selectedMin) : defaultMin;
	const resolvedMax = hasSelectedMax ? Number(facet.selectedMax) : defaultMax;

	if (resolvedMin === undefined || resolvedMax === undefined) {
		return null;
	}

	return { min: Number(resolvedMin), max: Number(resolvedMax) };
};

function buildFilters() {
	const [facets, setFacets] = useState<ApiFacetLoop54[]>([]);
	const [initialPriceMinMax, setInitialPriceMinMax] = useState<[number, number]>();
	const t = useTranslation().filtering;
	standardFacetNames.inStock = t.inStock;
	standardFacetNames.campaign = t.campaign;
	standardFacetNames.isEco = t.isEco;

	const isFacetRangeApplied = (facet: RangeFacet) => isRangeFacetApplied(facet, initialPriceMinMax);

	const getRangeSelectionForFacet = (facet: RangeFacet) => resolveRangeSelectionForFacet(facet, initialPriceMinMax);

	const setFilters = (facets: ApiFacetLoop54[]) => {
		setFacets(setItemDisplayNames(facets));
	};

	// sets display names for the standard filters
	const setItemDisplayNames = (facets: ApiFacetLoop54[]): ApiFacetLoop54[] => {
		return facets.map((facet) => {
			if (facet.type === 'distinct') {
				if (facet.name === 'inStock' && facet.items) {
					return {
						...facet,
						displayName: standardFacetNames['inStock'],
					};
				} else if (facet.name === 'campaign' && facet.items) {
					return {
						...facet,
						displayName: standardFacetNames['campaign'],
					};
				} else if (facet.name === 'isEco' && facet.items) {
					return {
						...facet,
						displayName: standardFacetNames['isEco'],
					};
				}
			}
			return facet;
		});
	};

	const resetFilters = () => {
		const resetTempFilters = facets.map((f) => {
			if (f.type === 'distinct' && f.items) {
				return {
					...f,
					items: f.items.map((item) => ({
						...item,
						selected: false,
					})),
				};
			} else if (f.type === 'range') {
				const isPriceFacet = f.name === 'price' && initialPriceMinMax;
				const defaultMin = isPriceFacet ? initialPriceMinMax![0] : f.min;
				const defaultMax = isPriceFacet ? initialPriceMinMax![1] : f.max;

				return {
					...f,
					min: defaultMin ?? f.min,
					max: defaultMax ?? f.max,
					selectedMin: undefined,
					selectedMax: undefined,
				};
			}
			return f;
		});
		setFacets(resetTempFilters);
	};

	const clearFilters = useCallback(() => {
		setFacets([]);
		setInitialPriceMinMax(undefined);
	}, []);

	const updateFilters = (
		filterName: string,
		itemValue: string | [number | undefined, number | undefined],
		isApplied: boolean
	) => {
		const updatedTempFilters = facets.map((filter) => {
			if (filter.name === filterName && filter.type === 'distinct' && filter.items) {
				return {
					...filter,
					items: filter.items.map((item) =>
						item.item === itemValue ? { ...item, selected: isApplied } : item
					),
				};
			}

			if (filter.name === filterName && filter.type === 'range') {
				if (!isApplied) {
					return {
						...filter,
						selectedMin: undefined,
						selectedMax: undefined,
					};
				}

				const [min, max] = itemValue as [number | undefined, number | undefined];
				return {
					...filter,
					min: filter.min,
					max: filter.max,
					selectedMin: min,
					selectedMax: max,
				};
			}

			return filter;
		});

		setFacets(updatedTempFilters);
	};

	const getAppliedFilters = () => {
		return facets
			.map((filter) => {
				if (filter.type === 'distinct' && filter.items) {
					const appliedItems = filter.items.filter((item) => item.selected);
					if (appliedItems.length > 0) {
						return { ...filter, items: appliedItems };
					}
				} else if (filter.type === 'range') {
					if (isFacetRangeApplied(filter)) {
						return filter;
					}
				}
				return null;
			})
			.filter(Boolean) as ApiFacetLoop54[];
	};

	const getNumOfAppliedFilters = () =>
		facets.reduce((count, filter) => {
			if (filter.type === 'distinct' && filter.items) {
				const appliedItems = filter.items.filter((item) => item.selected).length;
				return count + appliedItems;
			} else if (filter.type === 'range') {
				if (isFacetRangeApplied(filter)) {
					return count + 1;
				}
			}
			return count;
		}, 0);

	// return `attributeName` from these attribute strings: id_attributeName_(unit)
	const getAttributeNameFromAttributeString = (attributeString: string): string => {
		const firstUnderscore = attributeString.indexOf('_');
		const secondUnderscore = attributeString.indexOf('_', firstUnderscore + 1);
		if (firstUnderscore !== -1 && secondUnderscore !== -1) {
			return attributeString.slice(firstUnderscore + 1, secondUnderscore);
		}

		return attributeString;
	};

	// returns `unit`, if it exists, from these attribute strings: id_attributeName_(unit)
	const getUnitFromAttributeString = (attributeString: string): string | null => {
		const parenthesisStart = attributeString.indexOf('(');
		const parenthesisEnd = attributeString.indexOf(')', parenthesisStart);

		if (parenthesisStart !== -1 && parenthesisEnd !== -1 && parenthesisEnd > parenthesisStart + 1) {
			return attributeString.slice(parenthesisStart + 1, parenthesisEnd);
		}

		return null;
	};

	const setInitialMinMax = (initialMinMax: [number, number]) => {
		setInitialPriceMinMax([initialMinMax[0], initialMinMax[1]]);
	};

	const getInitialMinMax = () => initialPriceMinMax;

	return {
		filters: facets,
		setFilters,
		resetFilters,
		clearFilters,
		updateFilters,
		getNumOfAppliedFilters,
		getAppliedFilters,
		isRangeFacetApplied: isFacetRangeApplied,
		getRangeSelectionForFacet,
		getAttributeNameFromAttributeString,
		getUnitFromAttributeString,
		setInitialMinMax,
		getInitialMinMax,
	};
}

export const FilterContainer = createContainer(buildFilters);
